import React, { useContext, useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import StarekContext from "../context/StarekContext";
import Header from "../layouts/Header";
import Spinner from "../components/Spinner";
import "bootstrap/dist/css/bootstrap.min.css";
import "../starek.css";
import StarekPlansModal from "../components/modals/StarekPlansModal";
import ConfirmModal from "../components/modals/ConfirmModal";
import { CSSTransition, SwitchTransition } from "react-transition-group";
import { gql, useQuery, useMutation } from "@apollo/client";

// Confirmation messages
const PLANT_MESSAGES = {
    DELETE_PLANT: (plantName) => `You are about to delete plant "${plantName}".

This action cannot be undone. All associated zones, shifts, and data will be permanently deleted.

Are you sure you wish to continue?`,
};

const GET_PLANTS_LIST = gql`
    query GetPlants {
        plants {
            id
            name
            description
            is_active
            zones {
                name
                id
                parent_id
                description
                created_at
            }
            shifts {
                name
                id
                start_time
                end_time
                runs_on
            }
        }
    }
`;

const CREATE_ONE_PLANT_MUTATION = gql`
    mutation CreateOnePlant($data: PlantCreateInput!) {
        createOnePlant(data: $data) {
            id
            name
            description
            created_by {
                name
            }
            updated_by {
                name
            }
        }
    }
`;

const UPDATE_ONE_PLANT_MUTATION = gql`
    mutation UpdateOnePlant(
        $data: PlantUpdateInput!
        $where: PlantWhereUniqueInput!
    ) {
        updateOnePlant(data: $data, where: $where) {
            id
            name
            is_active
            updated_by {
                name
            }
        }
    }
`;

const DELETE_ONE_PLANT_MUTATION = gql`
    mutation DeleteOnePlant($where: PlantWhereUniqueInput!) {
        deleteOnePlant(where: $where) {
            id
            deleted_at
        }
    }
`;

const ChoosePlant = (props) => {
    const starekContext = useContext(StarekContext);
    const navigator = useNavigate();

    const { data, loading, error, refetch } = useQuery(GET_PLANTS_LIST, {
        client: starekContext.engineClient,
        onCompleted: (fetchedData) => {
            starekContext.setAvailablePlants(fetchedData.plants);
        },
    });

    const [createPlant, { loading: createLoading }] = useMutation(
        CREATE_ONE_PLANT_MUTATION,
        {
            client: starekContext.engineClient,
        }
    );

    const [updatePlant, { loading: updateLoading }] = useMutation(
        UPDATE_ONE_PLANT_MUTATION,
        {
            client: starekContext.engineClient,
        }
    );

    const [deletePlant, { loading: deleteLoading }] = useMutation(
        DELETE_ONE_PLANT_MUTATION,
        {
            client: starekContext.engineClient,
        }
    );

    const [slideOut, setSlideOut] = useState(false);
    const [showPlansModal, setShowPlansModal] = useState(false);
    const [showCreatePlantModal, setShowCreatePlantModal] = useState(false);
    const [plantName, setPlantName] = useState("");
    const [plantDescription, setPlantDescription] = useState("");
    const [plantNameError, setPlantNameError] = useState("");
    const [selectedPlantId, setSelectedPlantId] = useState(null);
    const [searchText, setSearchText] = useState("");
    const [actionMenuPlantId, setActionMenuPlantId] = useState(null);
    const [confirmCfg, setConfirmCfg] = useState({ open: false });

    const handleShowPlansModal = () => setShowPlansModal(true);
    const handleClosePlansModal = () => setShowPlansModal(false);
    const handleShowCreatePlantModal = () => {
        setPlantName("");
        setPlantDescription("");
        setPlantNameError("");
        setShowCreatePlantModal(true);
    };
    const handleCloseCreatePlantModal = () => {
        setShowCreatePlantModal(false);
    };

    const handleCardClick = async (plantData) => {
        // Prevent selection of inactive plants
        if (!plantData.is_active) {
            return;
        }

        setSelectedPlantId(plantData.id);

        setTimeout(() => {
            starekContext.setLastUsedPlant(plantData);
            setSlideOut(true);
            setTimeout(() => {
                navigator("/dashboard");
            }, 300);
        }, 600);
    };

    const openConfirm = (cfg) => setConfirmCfg({ open: true, ...cfg });
    const closeConfirm = () => setConfirmCfg({ open: false });

    const handleToggleActive = async (event, plantId, currentState) => {
        event.stopPropagation();
        try {
            await updatePlant({
                variables: {
                    data: {
                        is_active: { set: !currentState },
                        updated_by: {
                            connect: { id: parseInt(starekContext.user.id) },
                        },
                    },
                    where: { id: plantId },
                },
            });
            setActionMenuPlantId(null);
            refetch();
        } catch (err) {
            starekContext.showError(err);
        }
    };

    const handleDeletePlant = (event, plant) => {
        event.stopPropagation();
        openConfirm({
            title: "Delete Plant?",
            message: PLANT_MESSAGES.DELETE_PLANT(plant.name),
            confirmText: "Delete",
            cancelText: "Cancel",
            type: "danger",
            count: 1,
            onConfirm: async () => {
                try {
                    await deletePlant({
                        variables: {
                            where: { id: plant.id },
                        },
                    });
                    setActionMenuPlantId(null);
                    refetch();
                    closeConfirm();
                } catch (err) {
                    starekContext.showError(err);
                    closeConfirm();
                }
            },
            onClose: closeConfirm,
            isLoading: deleteLoading,
        });
    };

    const handleCreatePlant = async (event) => {
        try {
            if (event) {
                event.preventDefault();
            }

            setPlantNameError("");

            if (!plantName.trim()) {
                setPlantNameError("Plant name is required");
                return;
            }

            await createPlant({
                variables: {
                    data: {
                        name: plantName.trim(),
                        description: plantDescription.trim(),
                        created_by: {
                            connect: { id: parseInt(starekContext.user.id) },
                        },
                        updated_by: {
                            connect: { id: parseInt(starekContext.user.id) },
                        },
                    },
                },
            });

            refetch();
            handleCloseCreatePlantModal();
        } catch (err) {
            starekContext.showError(err);
        }
    };

    const plantsList = data?.plants ?? starekContext.availablePlants ?? [];
    const currentLicense = starekContext?.lastUsedWorkspace?.licenses?.[0];
    const plantsLimit = currentLicense?.plants || "Unlimited";
    const currentPlantCount = plantsList.length;
    const canCreateMorePlants =
        plantsLimit === "Unlimited" || currentPlantCount < plantsLimit;

    // Filter plants based on search text
    const filteredPlantsList = plantsList.filter(
        (plant) =>
            plant.name.toLowerCase().includes(searchText.toLowerCase()) ||
            (plant.description &&
                plant.description
                    .toLowerCase()
                    .includes(searchText.toLowerCase()))
    );

    useEffect(() => {
        if (data?.plants) {
            starekContext.setAvailablePlants(data.plants);
        }
    }, [data]);

    useEffect(() => {
        if (starekContext?.lastUsedPlant?.id) {
            setSelectedPlantId(starekContext.lastUsedPlant.id);
        }
    }, []);

    useEffect(() => {
        if (
            selectedPlantId &&
            starekContext?.lastUsedPlant?.id === selectedPlantId
        ) {
            setSlideOut(true);
            setTimeout(() => {
                const redirectPath =
                    sessionStorage.getItem("postAuthRedirect") || "/";
                navigator(redirectPath);
            }, 300);
        }
    }, [starekContext?.lastUsedPlant?.id]);

    return (
        <SwitchTransition>
            <CSSTransition
                key={slideOut ? "exit" : "enter"}
                timeout={300}
                classNames={{
                    enter: "page-crossfade-enter",
                    enterActive: "page-crossfade-enter-active",
                    exit: "page-crossfade-exit",
                    exitActive: "page-crossfade-exit-active",
                }}
            >
                <div className="transition-container">
                    <div className="choose-plant-page">
                        <div
                            style={{
                                maxWidth: "1400px",
                                margin: "0 auto",
                                width: "100%",
                                padding: "0 2rem",
                            }}
                        >
                            <Header hasLeftMargin={false} />
                        </div>
                        <div className="choose-plant-content">
                            <div className="choose-plant-container">
                                <div className="subscription-card">
                                    <div className="subscription-card-inner">
                                        <div className="subscription-info">
                                            <div className="subscription-plan-name">
                                                {currentLicense?.name ||
                                                    "Basic Plan"}
                                            </div>
                                            <div className="subscription-usage">
                                                <div className="subscription-stat">
                                                    <i className="fa fa-leaf text-success"></i>
                                                    <span>
                                                        {currentPlantCount}/
                                                        {plantsLimit} plants
                                                        created
                                                    </span>
                                                </div>
                                                <div className="subscription-stat">
                                                    <i className="fa fa-microchip"></i>
                                                    <span>
                                                        {currentLicense?.devices ||
                                                            "Unlimited"}{" "}
                                                        Devices
                                                    </span>
                                                </div>
                                            </div>
                                            <div className="subscription-badges">
                                                {currentPlantCount >= 8 &&
                                                    plantsLimit !==
                                                        "Unlimited" && (
                                                        <span className="badge bg-warning text-dark">
                                                            Nearing Limit
                                                        </span>
                                                    )}
                                                {!canCreateMorePlants && (
                                                    <span className="badge bg-danger">
                                                        Limit Reached
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                        {!canCreateMorePlants && (
                                            <div className="subscription-actions">
                                                <button
                                                    className="btn btn-warning"
                                                    onClick={
                                                        handleShowPlansModal
                                                    }
                                                >
                                                    Upgrade Plan
                                                </button>
                                            </div>
                                        )}
                                        {canCreateMorePlants && (
                                            <div className="subscription-actions">
                                                <button
                                                    className="btn btn-outline-secondary"
                                                    onClick={
                                                        handleShowPlansModal
                                                    }
                                                    title="View available plans and upgrade"
                                                >
                                                    <i className="fa fa-crown me-2"></i>
                                                    View / Upgrade Plan
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {!loading && plantsList.length > 0 && (
                                    <div>
                                        <h5
                                            className="plants-section-title"
                                            style={{ marginBottom: "1.5rem" }}
                                        >
                                            Plants
                                        </h5>
                                        <div
                                            style={{
                                                display: "flex",
                                                gap: "0.75rem",
                                                marginBottom: "1.5rem",
                                                alignItems: "flex-end",
                                            }}
                                        >
                                            <div style={{ flex: 1 }}>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    placeholder="Search plants..."
                                                    value={searchText}
                                                    onChange={(e) =>
                                                        setSearchText(
                                                            e.target.value
                                                        )
                                                    }
                                                />
                                            </div>
                                            <button
                                                className="btn btn-primary"
                                                onClick={
                                                    handleShowCreatePlantModal
                                                }
                                                disabled={!canCreateMorePlants}
                                                title={
                                                    !canCreateMorePlants
                                                        ? "Plant limit reached. Upgrade to create more."
                                                        : "Create a new plant"
                                                }
                                            >
                                                <i className="fa fa-plus me-2"></i>
                                                Add Plant
                                            </button>
                                        </div>
                                    </div>
                                )}

                                <div className="plants-grid">
                                    {loading ? (
                                        <div className="loading-state-card">
                                            <Spinner />
                                            <div className="loading-state-title">
                                                Loading Your Plants
                                            </div>
                                            <p className="loading-state-text">
                                                Please wait while we fetch your
                                                plants...
                                            </p>
                                        </div>
                                    ) : plantsList.length === 0 ? (
                                        <div className="empty-state-card">
                                            <div className="empty-state-icon">
                                                <i className="fa fa-industry"></i>
                                            </div>
                                            <h5 className="empty-state-title">
                                                No Plants Yet
                                            </h5>
                                            <p className="empty-state-text">
                                                You haven't created any plants
                                                yet. Get started by creating
                                                your first plant to begin
                                                managing your production.
                                            </p>
                                            <div className="empty-state-cta-group">
                                                <button
                                                    className="btn btn-primary"
                                                    onClick={
                                                        handleShowCreatePlantModal
                                                    }
                                                >
                                                    <i className="fa fa-plus me-2"></i>
                                                    Create Your First Plant
                                                </button>
                                                <button
                                                    className="btn btn-outline-info"
                                                    onClick={
                                                        handleShowPlansModal
                                                    }
                                                >
                                                    <i className="fa fa-crown me-2"></i>
                                                    View Plans
                                                </button>
                                            </div>
                                        </div>
                                    ) : searchText &&
                                      filteredPlantsList.length === 0 ? (
                                        <div className="empty-state-card">
                                            <div className="empty-state-icon">
                                                <i className="fa fa-search"></i>
                                            </div>
                                            <h5 className="empty-state-title">
                                                No Plants Found
                                            </h5>
                                            <p className="empty-state-text">
                                                No plants match your search "
                                                {searchText}". Try adjusting
                                                your search terms.
                                            </p>
                                            <button
                                                className="btn btn-outline-secondary mt-3"
                                                onClick={() =>
                                                    setSearchText("")
                                                }
                                            >
                                                <i className="fa fa-times me-2"></i>
                                                Clear Search
                                            </button>
                                        </div>
                                    ) : (
                                        filteredPlantsList.map((plant) => (
                                            <div
                                                key={plant.id}
                                                onClick={() =>
                                                    handleCardClick(plant)
                                                }
                                                className={`plant-card ${
                                                    selectedPlantId === plant.id
                                                        ? "selected"
                                                        : ""
                                                } ${
                                                    !plant.is_active
                                                        ? "disabled"
                                                        : ""
                                                }`}
                                            >
                                                {selectedPlantId ===
                                                    plant.id && (
                                                    <div className="plant-selected-badge">
                                                        <i className="fa fa-check"></i>
                                                        Selected
                                                    </div>
                                                )}
                                                {selectedPlantId !==
                                                    plant.id && (
                                                    <>
                                                        <button
                                                            className="plant-card-menu-btn"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setActionMenuPlantId(
                                                                    actionMenuPlantId ===
                                                                        plant.id
                                                                        ? null
                                                                        : plant.id
                                                                );
                                                            }}
                                                            title="Plant options"
                                                        >
                                                            <i className="fa fa-ellipsis-v"></i>
                                                        </button>
                                                        {actionMenuPlantId ===
                                                            plant.id && (
                                                            <div className="plant-card-menu">
                                                                <button
                                                                    className="plant-menu-item"
                                                                    onClick={(
                                                                        e
                                                                    ) =>
                                                                        handleToggleActive(
                                                                            e,
                                                                            plant.id,
                                                                            plant.is_active
                                                                        )
                                                                    }
                                                                    disabled={
                                                                        updateLoading ||
                                                                        selectedPlantId ===
                                                                            plant.id
                                                                    }
                                                                    title={
                                                                        selectedPlantId ===
                                                                        plant.id
                                                                            ? "Cannot modify the currently selected plant"
                                                                            : ""
                                                                    }
                                                                >
                                                                    <i
                                                                        className={`fa fa-${
                                                                            plant.is_active
                                                                                ? "ban"
                                                                                : "check"
                                                                        } me-2`}
                                                                    ></i>
                                                                    {plant.is_active
                                                                        ? "Make Inactive"
                                                                        : "Make Active"}
                                                                </button>
                                                                <button
                                                                    className="plant-menu-item text-danger"
                                                                    onClick={(
                                                                        e
                                                                    ) =>
                                                                        handleDeletePlant(
                                                                            e,
                                                                            plant
                                                                        )
                                                                    }
                                                                    disabled={
                                                                        deleteLoading ||
                                                                        selectedPlantId ===
                                                                            plant.id
                                                                    }
                                                                    title={
                                                                        selectedPlantId ===
                                                                        plant.id
                                                                            ? "Cannot delete the currently selected plant"
                                                                            : ""
                                                                    }
                                                                >
                                                                    <i className="fa fa-trash me-2"></i>
                                                                    Delete
                                                                </button>
                                                            </div>
                                                        )}
                                                    </>
                                                )}
                                                <div className="plant-card-content">
                                                    <div
                                                        className="plant-icon"
                                                        style={{
                                                            borderRadius:
                                                                "12px",
                                                        }}
                                                    >
                                                        <i className="fa fa-industry"></i>
                                                    </div>
                                                    <h5 className="plant-title">
                                                        {plant.name}
                                                    </h5>
                                                    <p className="plant-description">
                                                        {plant.description ||
                                                            "No description provided"}
                                                    </p>
                                                    <div className="plant-status">
                                                        <span
                                                            className={`plant-status-indicator ${
                                                                plant.is_active
                                                                    ? "active"
                                                                    : "inactive"
                                                            }`}
                                                        ></span>
                                                        <span>
                                                            {plant.is_active
                                                                ? "Active"
                                                                : "Inactive"}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        ))
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    {showCreatePlantModal && (
                        <div
                            className="modal fade show"
                            style={{
                                display: "block",
                                backgroundColor: "rgba(0,0,0,0.5)",
                            }}
                            tabIndex="-1"
                            role="dialog"
                            aria-labelledby="createPlantModal"
                            aria-hidden="true"
                        >
                            <div
                                className="modal-dialog modal-lg modal-dialog-centered"
                                role="document"
                            >
                                <div className="modal-content rounded-lg shadow-lg">
                                    <div className="modal-header bg-light border-bottom">
                                        <h5
                                            className="modal-title font-weight-bold text-primary"
                                            id="createPlantModal"
                                        >
                                            <i className="fa fa-plus me-2"></i>
                                            Create New Plant
                                        </h5>
                                        <button
                                            type="button"
                                            className="btn-close"
                                            onClick={
                                                handleCloseCreatePlantModal
                                            }
                                            aria-label="Close"
                                        ></button>
                                    </div>
                                    <div className="modal-body p-4">
                                        <form
                                            onSubmit={(e) => {
                                                e.preventDefault();
                                                handleCreatePlant();
                                            }}
                                        >
                                            <div className="mb-3">
                                                <label
                                                    htmlFor="plantName"
                                                    className="form-label fw-600"
                                                >
                                                    Plant Name{" "}
                                                    <span className="text-danger">
                                                        *
                                                    </span>
                                                </label>
                                                <input
                                                    type="text"
                                                    className={`form-control ${
                                                        plantNameError
                                                            ? "is-invalid"
                                                            : ""
                                                    }`}
                                                    id="plantName"
                                                    placeholder="e.g., Factory A, Main Plant"
                                                    value={plantName}
                                                    onChange={(e) => {
                                                        setPlantName(
                                                            e.target.value
                                                        );
                                                        if (plantNameError)
                                                            setPlantNameError(
                                                                ""
                                                            );
                                                    }}
                                                />
                                                {plantNameError && (
                                                    <div className="invalid-feedback d-block">
                                                        {plantNameError}
                                                    </div>
                                                )}
                                            </div>

                                            <div className="mb-4">
                                                <label
                                                    htmlFor="plantDescription"
                                                    className="form-label fw-600"
                                                >
                                                    Description
                                                </label>
                                                <textarea
                                                    className="form-control"
                                                    id="plantDescription"
                                                    rows="4"
                                                    placeholder="Optional: Describe your plant location, purpose, or other relevant details"
                                                    value={plantDescription}
                                                    onChange={(e) =>
                                                        setPlantDescription(
                                                            e.target.value
                                                        )
                                                    }
                                                ></textarea>
                                                <small className="text-muted d-block mt-2">
                                                    <i className="fa fa-info-circle me-1"></i>
                                                    You can configure zones,
                                                    shifts, cycles, and other
                                                    details after creation
                                                </small>
                                            </div>
                                        </form>
                                    </div>
                                    <div className="modal-footer bg-light border-top">
                                        <button
                                            type="button"
                                            className="btn btn-secondary"
                                            onClick={
                                                handleCloseCreatePlantModal
                                            }
                                            disabled={createLoading}
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="button"
                                            className="btn btn-primary"
                                            onClick={handleCreatePlant}
                                            disabled={createLoading}
                                        >
                                            {createLoading ? (
                                                <>
                                                    <span
                                                        className="spinner-border spinner-border-sm me-2"
                                                        role="status"
                                                        aria-hidden="true"
                                                    ></span>
                                                    Creating...
                                                </>
                                            ) : (
                                                <>
                                                    <i className="fa fa-check me-2"></i>
                                                    Create Plant
                                                </>
                                            )}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Plans Modal */}
                    {showPlansModal && (
                        <StarekPlansModal
                            show={showPlansModal}
                            handleClose={handleClosePlansModal}
                        />
                    )}

                    {/* Delete Confirmation Modal */}
                    {confirmCfg.open && (
                        <ConfirmModal
                            open={confirmCfg.open}
                            type={confirmCfg.type}
                            title={confirmCfg.title}
                            message={confirmCfg.message}
                            confirmText={confirmCfg.confirmText}
                            cancelText={confirmCfg.cancelText}
                            onConfirm={confirmCfg.onConfirm}
                            onClose={confirmCfg.onClose}
                            isLoading={confirmCfg.isLoading}
                        />
                    )}
                </div>
            </CSSTransition>
        </SwitchTransition>
    );
};

export default ChoosePlant;
